/**
 * Minimax AI - "Tactician"
 *
 * Uses minimax search with alpha-beta pruning to deeply analyze tactical positions.
 * Strengths:
 * - Excellent at calculating forced sequences
 * - Strong tactical play (captures, threats)
 * - Deterministic and explainable
 *
 * Weaknesses:
 * - Can struggle with complex positional play
 * - Depth limited by time constraints
 */

import { GameState, IMove, Position, PieceType, MoveType } from "../../types";
import { IAIPlayer, AIMoveResult, AIStats, AIPlayerType } from "../Core/IAIPlayer";
import { ZodiacEngine } from "../../zodiacEngine";
import { MinimaxEngine, MinimaxModel } from "./MinimaxEngine";
import { PositionEvaluator } from "./PositionEvaluator";
import { selectKingRandom, selectPieceStrategic, selectPieceStrategicWithReasoning, PieceSelectionReasoning } from "./PieceSelector";
import { getLegalMoves, simulateMove } from "../Core/CommonAIUtils";
import { ZodiacFEN } from "../../Fen/ZodiacFEN";

export class MinimaxAI implements IAIPlayer {
  readonly name = AIPlayerType.MINIMAX;
  readonly displayName = "Tactician";
  readonly description = "Deep tactical analyzer using minimax with alpha-beta pruning";

  private minimaxEngine: MinimaxEngine;
  private gameEngine: ZodiacEngine;
  private stats: AIStats = {
    movesCalculated: 0,
    averageThinkingTime: 0,
    nodesEvaluated: 0,
  };
  private totalThinkingTime = 0;
  private totalNodes = 0;

  constructor(gameEngine: ZodiacEngine, model: MinimaxModel = MinimaxModel.SENTINEL) {
    this.gameEngine = gameEngine;
    this.minimaxEngine = new MinimaxEngine(gameEngine, model);
  }

  /**
   * Set the minimax model to use
   */
  setModel(model: MinimaxModel): void {
    this.minimaxEngine.setModel(model);
  }

  /**
   * Cap iterative-deepening depth. Use for tutorials where deep search makes
   * Sentinel play randomly because every line ties at the loss score.
   */
  setMaxDepthCap(depth: number): void {
    this.minimaxEngine.setMaxDepthCap(depth);
  }

  /**
   * Select king randomly (as per requirements)
   */
  selectKing(availableKings: PieceType[], isLightSide: boolean): PieceType {
    const selectedKing = selectKingRandom(availableKings);
    return selectedKing;
  }

  /**
   * Select a piece strategically based on heuristics
   * Used during piece drafting phase
   */
  selectPiece(
    availablePieces: PieceType[],
    opponentPieces: PieceType[],
    ownPieces: PieceType[]
  ): PieceType {
    const selectedPiece = selectPieceStrategic(availablePieces, opponentPieces, ownPieces);
    return selectedPiece;
  }

  /**
   * Select a piece strategically with detailed reasoning
   * Returns both the piece and the reasoning for selection
   */
  selectPieceWithReasoning(
    availablePieces: PieceType[],
    opponentPieces: PieceType[],
    ownPieces: PieceType[]
  ): PieceSelectionReasoning {
    return selectPieceStrategicWithReasoning(availablePieces, opponentPieces, ownPieces);
  }

  async selectMove(gameState: GameState, maxThinkingTime: number): Promise<AIMoveResult> {
    const startTime = Date.now();

    // Run minimax search
    const result = this.minimaxEngine.findBestMove(gameState, maxThinkingTime);

    if (!result) {
      // Before throwing error, check if this is a Virus with forced end turn
      // This can happen when Virus exhausts all infection targets during bonus turn
      const allMoves = getLegalMoves(gameState);
      if (allMoves.length === 1 && allMoves[0].move.moveType === MoveType.endTurn) {
        const forcedMove = allMoves[0];
        const thinkingTime = Date.now() - startTime;

        return {
          fromPosition: forcedMove.fromPosition,
          move: forcedMove.move,
          confidence: 1.0, // Forced move = 100% confidence
          thinkingTime,
          reasoning: 'Forced end turn - Virus exhausted all infection targets'
        };
      }

      throw new Error("Tactician AI failed to find a valid move");
    }

    const thinkingTime = Date.now() - startTime;
    const nodesSearched = this.minimaxEngine.getNodesSearched();

    // Update stats
    this.stats.movesCalculated++;
    this.totalThinkingTime += thinkingTime;
    this.totalNodes += nodesSearched;
    this.stats.averageThinkingTime = this.totalThinkingTime / this.stats.movesCalculated;
    this.stats.nodesEvaluated = this.totalNodes;

    // Calculate confidence based on search depth and score
    const confidence = this.calculateConfidence(result.score, nodesSearched);

    // Get position evaluation for reporting
    const evaluation = result.score;

    // Get evaluation breakdown if using Sentinel model
    let evaluationBreakdown = undefined;
    if (this.minimaxEngine['model'] === 'sentinel') {
      evaluationBreakdown = require('./SentinelEvaluator').SentinelEvaluator.getLastBreakdown();
    } else {
    }

    // Get principal variation from MinimaxEngine and enrich with FENs for board playback
    const principalVariation = MinimaxEngine.lastPV || [];

    if (principalVariation.length > 0) {
      let currentState = gameState;
      for (const pvNode of principalVariation) {
        const nextState = simulateMove(currentState, { fromPosition: pvNode.fromPosition, move: pvNode.move });
        if (nextState) {
          pvNode.fen = ZodiacFEN.encode(nextState);
          currentState = nextState;
        } else {
          break;
        }
      }
    }

    // Calculate initial board value (absolute Light-Dark evaluation before first PV move)
    // CRITICAL: Use getAbsoluteBoardValue to get consistent (Light - Dark) evaluation
    const initialBoardValue = require('./SentinelEvaluator').SentinelEvaluator.getAbsoluteBoardValue(gameState);

    // Get the moving piece information for display
    const fromSquare = gameState.board[result.fromPosition.row][result.fromPosition.col];
    const movingPiece = fromSquare.Pieces.find(p => p.id === result.move.pieceId);

    // Get root move scores for diagnostic display
    const rootMoveScores = MinimaxEngine.lastRootScores;

    return {
      fromPosition: result.fromPosition,
      move: result.move,
      confidence,
      thinkingTime,
      evaluation,
      reasoning: `Depth ${result.depth || 0} | nodes=${nodesSearched} | score: ${result.score.toFixed(0)}`,
      evaluationBreakdown,
      movingPiece: movingPiece ? { type: movingPiece.type, isKing: movingPiece.isKing } : undefined,
      principalVariation,
      initialBoardValue,
      rootMoveScores,
    };
  }

  getStats(): AIStats {
    return { ...this.stats };
  }

  reset(): void {
    this.stats = {
      movesCalculated: 0,
      averageThinkingTime: 0,
      nodesEvaluated: 0,
    };
    this.totalThinkingTime = 0;
    this.totalNodes = 0;
  }

  configure(config: Record<string, any>): void {
    // Could expose depth limits, evaluation weights, etc.
  }

  /**
   * Calculate confidence based on minimax score and nodes searched
   */
  private calculateConfidence(score: number, nodesSearched: number): number {
    let confidence = 0.5;

    // High scores indicate strong moves
    if (Math.abs(score) > 10000) {
      confidence = 0.95; // Very confident about winning/losing positions
    } else if (Math.abs(score) > 5000) {
      confidence = 0.85; // Confident about strong advantage
    } else if (Math.abs(score) > 1000) {
      confidence = 0.75; // Moderate advantage
    } else {
      confidence = 0.6; // Balanced position
    }

    // More nodes searched = higher confidence
    const nodeFactor = Math.min(1.0, nodesSearched / 10000) * 0.1;
    confidence = Math.min(0.95, confidence + nodeFactor);

    return confidence;
  }
}

export { MinimaxEngine, MinimaxModel } from "./MinimaxEngine";
export { PositionEvaluator } from "./PositionEvaluator";
export { SentinelEvaluator } from "./SentinelEvaluator";
export { HunterEvaluator } from "./HunterEvaluator";
export { ArrowEvaluator } from "./ArrowEvaluator";
export type { EvaluationBreakdown } from "./SentinelEvaluator";
export type { PieceSelectionReasoning } from "./PieceSelector";